/**
 * Browser Logger
 * Client-side logging that works in the browser (no Winston/process dependency)
 */

import { getEnvironmentConfig } from '../../config/environment/environment';
import { storage } from '../services/StorageService';

export interface LogEntry {
  level: 'debug' | 'info' | 'warn' | 'error';
  message: string;
  timestamp: Date;
  service: string;
  data?: any;
}

export class BrowserLogger {
  private static instance: BrowserLogger;
  private logBuffer: LogEntry[] = [];
  private readonly maxBufferSize = 100;
  private remoteLoggingEnabled = false;
  // No initial value — set in constructor from environment.apiUrl. The
  // hard-coded 'http://localhost:2567/api/logs' default leaked into
  // production bundles and tripped the Arc A.2 bundle-check guard.
  private remoteEndpoint = '';
  private environment = getEnvironmentConfig();
  private lastRemoteAttempt = 0;
  private readonly remoteThrottleMs = 5000; // Throttle remote logging to prevent spam

  private constructor() {
    // Disable remote logging in development mode to prevent CORS spam
    if (this.environment.isLocalhost) {
      console.log('📡 Remote logging disabled in development mode');
      this.remoteLoggingEnabled = false;
      return;
    }
    
    // For production, check if remote logging should be enabled
    // Check if user explicitly enabled it
    const forceRemote = (typeof (globalThis as any).window !== 'undefined') 
      ? storage.get('helix_force_remote_logging') 
      : null;
      
    if (forceRemote === 'true') {
      this.remoteLoggingEnabled = true;
      this.remoteEndpoint = `${this.environment.apiUrl}/logs`;
      console.log('📡 Remote logging force-enabled');
      return;
    }
    
    // Check if remote logging is enabled (only in production).
    // Probe /api/version (which exists, returns 200) instead of the
    // legacy /api/status (which doesn't exist — was 404'ing in the
    // browser console on every fresh load).
    if (this.environment.isProduction && typeof fetch !== 'undefined') {
      fetch(`${this.environment.apiUrl}/version`).then(() => {
        this.remoteLoggingEnabled = true;
        this.remoteEndpoint = `${this.environment.apiUrl}/logs`;
        if (typeof (globalThis as any).window !== 'undefined') console.log('📡 Remote logging enabled');
      }).catch(() => {
        this.remoteLoggingEnabled = false; // Explicitly disable on error
        if (typeof (globalThis as any).window !== 'undefined') console.log('📡 Remote logging disabled (server not ready)');
      });
    } else {
      this.remoteLoggingEnabled = false; // Explicitly disable if not production
    }
  }

  public static getInstance(): BrowserLogger {
    if (!BrowserLogger.instance) {
      BrowserLogger.instance = new BrowserLogger();
    }
    return BrowserLogger.instance;
  }

  /**
   * Log debug message
   */
  public debug(message: string, data?: any): void {
    this.log('debug', message, data);
  }

  /**
   * Log info message
   */
  public info(message: string, data?: any): void {
    this.log('info', message, data);
  }

  /**
   * Log warning message
   */
  public warn(message: string, data?: any): void {
    this.log('warn', message, data);
  }

  /**
   * Log error message
   */
  public error(message: string, data?: any): void {
    this.log('error', message, data);
  }

  /**
   * Internal log method
   */
  private log(level: 'debug' | 'info' | 'warn' | 'error', message: string, data?: any): void {
    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date(),
      service: 'helix2000-client',
      data
    };

    // Add to buffer
    this.logBuffer.push(entry);
    if (this.logBuffer.length > this.maxBufferSize) {
      this.logBuffer.shift();
    }

    // Output to console with proper level
    const timestamp = entry.timestamp.toISOString();
    const coloredMessage = `[${timestamp}] [${level.toUpperCase()}] ${message}`;

    switch (level) {
    case 'debug':
      console.debug(`%c${coloredMessage}`, 'color: #888;', data || '');
      break;
    case 'info':
      console.info(`%c${coloredMessage}`, 'color: #0c0;', data || '');
      break;
    case 'warn':
      console.warn(`%c${coloredMessage}`, 'color: #c80;', data || '');
      break;
    case 'error':
      console.error(`%c${coloredMessage}`, 'color: #c00;', data || '');
      break;
    }

    // Send to server if remote logging is enabled
    this.sendToServer(entry);
  }

  /**
   * Send log entry to server
   */
  private async sendToServer(_entry: LogEntry): Promise<void> {
    if (!this.remoteLoggingEnabled) return;

    // Throttle remote logging to prevent spam
    const now = Date.now();
    if (now - this.lastRemoteAttempt < this.remoteThrottleMs) {
      return;
    }
    this.lastRemoteAttempt = now;

    // Batch send to avoid too many requests
    if (this.logBuffer.length % 10 === 0) {
      try {
        const batch = this.logBuffer.slice(-10);
        await fetch(this.remoteEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ logs: batch }),
        });
      } catch (err) {
        // Silent fail - logging should never break the game
        // In production, you might want to queue these for later
        if (!this.environment.isLocalhost) {
          console.debug('Failed to send logs to server', err);
        }
        // If we get CORS/fetch errors, disable remote logging to prevent spam
        this.remoteLoggingEnabled = false;
        console.log('📡 Remote logging auto-disabled due to connection issues');
      }
    }
  }

  /**
   * Get recent logs
   */
  public getRecentLogs(count: number = 10): LogEntry[] {
    return this.logBuffer.slice(-count);
  }

  /**
   * Clear log buffer
   */
  public clear(): void {
    this.logBuffer = [];
    console.log('🧹 Log buffer cleared');
  }

  /**
   * Configure remote logging
   */
  public configureRemoteLogging(enabled: boolean, endpoint?: string): void {
    this.remoteLoggingEnabled = enabled;
    if (endpoint) {
      this.remoteEndpoint = endpoint;
    }
  }
}

// Convenience functions
export const browserLogger = BrowserLogger.getInstance();

export const logDebug = (message: string, data?: any) => {
  browserLogger.debug(message, data);
};

export const logInfo = (message: string, data?: any) => {
  browserLogger.info(message, data);
};

export const logWarn = (message: string, data?: any) => {
  browserLogger.warn(message, data);
};

export const logError = (message: string, data?: any) => {
  browserLogger.error(message, data);
};